import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { ExternalLink, Zap, Package, Clock, Link2, Layers, Boxes } from 'lucide-react';
import { t } from '@/i18n';
import { getSlotDetails, getFraction, getPackagingLine } from '@/data/mockData';

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/90 backdrop-blur-md border border-border rounded-xl shadow-lg px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{payload[0].value}t</p>
    </div>
  );
};

export const ProductionOrderDialog = ({ open, onOpenChange, slot, lineName, lineId, onNavigateToOrder }) => {
  const progressPct = slot?.actualOutput != null && slot?.plannedOutput > 0
    ? Math.round((slot.actualOutput / slot.plannedOutput) * 100)
    : null;

  // Deterministic hourly output (seeded from orderId + day)
  const hourlyData = useMemo(() => {
    if (!slot) return [];
    const seed = (slot.orderId || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return Array.from({ length: 8 }, (_, i) => ({
      hour: `${6 + i * 2}:00`,
      output: Math.round((slot.plannedOutput / 8) * (0.7 + ((seed * (i + 1) * 13) % 100) / 167)),
    }));
  }, [slot]);

  const details = slot && lineId ? getSlotDetails(lineId, slot.day) : null;
  const fraction = details ? getFraction(details.fractionId) : null;
  const packagingLine = details ? getPackagingLine(details.packagingLineId) : null;
  const linkedDeps = details && details.linkedTo && details.linkedTo.length > 0 ? details.linkedTo : null;

  if (!slot || !slot.material) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-white/95 backdrop-blur-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{lineName} — {t(`mock.day.${slot.day}`)}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Material & Grade */}
          <div className="bg-muted/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-4 w-4 text-primary" />
              <span className="font-semibold">{slot.material}</span>
            </div>
            <div className="text-sm text-muted-foreground">{slot.grade}</div>
          </div>

          {/* Order link */}
          {slot.orderId && (
            <div
              className="flex items-center justify-between bg-muted/50 rounded-xl p-3 cursor-pointer hover:bg-muted/70 transition-colors"
              onClick={() => onNavigateToOrder?.(slot.orderId)}
            >
              <div>
                <div className="text-xs text-muted-foreground">{t('prodDialog.order')}</div>
                <div className="font-semibold text-primary">{slot.orderId}</div>
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </div>
          )}

          {/* Production metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/50 rounded-xl p-3 text-center">
              <div className="text-xs text-muted-foreground">{t('prodDialog.utilization')}</div>
              <div className="text-xl font-bold">{slot.utilization}%</div>
            </div>
            <div className="bg-muted/50 rounded-xl p-3 text-center">
              <div className="text-xs text-muted-foreground">{t('prodDialog.planned')}</div>
              <div className="text-xl font-bold">{slot.plannedOutput}t</div>
            </div>
            <div className="bg-muted/50 rounded-xl p-3 text-center">
              <div className="text-xs text-muted-foreground">{t('prodDialog.actual')}</div>
              <div className="text-xl font-bold">{slot.actualOutput ?? '—'}</div>
            </div>
          </div>

          {/* Progress bar */}
          {progressPct !== null && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('prodDialog.productionProgress')}</span>
                <span className="font-semibold text-foreground">{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="h-2" />
            </div>
          )}

          {/* Energy & Shift */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={slot.energyTariff === 'offpeak' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
              <Zap className="h-3 w-3 mr-1" />
              {slot.energyTariff === 'offpeak' ? t('prodDialog.offPeak') : t('prodDialog.peak')}
            </Badge>
            {slot.shift && (
              <Badge variant="outline">
                <Clock className="h-3 w-3 mr-1" />
                {slot.shift.charAt(0).toUpperCase() + slot.shift.slice(1)} {t('prodDialog.shift')}
              </Badge>
            )}
            {fraction && (
              <Badge variant="outline" style={{ borderColor: fraction.color }}>
                <Layers className="h-3 w-3 mr-1" />
                {t('planning.fraction')}: {t(fraction.labelKey)} · ×{fraction.cycleMultiplier.toFixed(1)}
              </Badge>
            )}
            {packagingLine && (
              <Badge variant="outline">
                <Boxes className="h-3 w-3 mr-1" />
                {t(packagingLine.labelKey)}
              </Badge>
            )}
          </div>

          {/* Linked production dependencies */}
          {linkedDeps && (
            <div className="bg-violet-50/60 border border-violet-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Link2 className="h-4 w-4 text-violet-700" />
                <span className="text-sm font-semibold text-violet-900">{t('planning.linkedDeps')}</span>
              </div>
              <div className="text-xs text-violet-800/80 mb-2">
                {t('planning.linkedDepsDesc')}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {linkedDeps.map(key => (
                  <Badge key={key} variant="outline" className="bg-white/70 border-violet-300 text-violet-900 font-mono">
                    {key}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Hourly output chart */}
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">{t('prodDialog.hourlyOutput')}</div>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={hourlyData}>
                <XAxis dataKey="hour" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis fontSize={10} tickLine={false} axisLine={false} hide />
                <Tooltip content={<GlassTooltip />} />
                <Bar dataKey="output" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} opacity={0.8} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
